<script setup lang="ts">
// swiper
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';
import 'swiper/css/pagination';
import 'swiper/css';

// components
import SvgIcon from '~/components/SvgIcon.vue';
import Buttons from '~/components/redesign/Buttons';
import AppAvatar from '~/components/redesign/AppAvatar.vue';
import AppTable from '~/components/redesign/AppTable';
import AppPagination from '~/components/redesign/AppPagination.vue';
import MultiselectDropdown from '~/components/redesign/MultiselectDropdown.vue';
import Cards from '~/components/redesign/Cards';

// composables
import { useI18n } from '#i18n';
import useApiSeo from '~/composables/useApiSeo';
import { useWindowSize, useMounted } from '@vueuse/core';
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// utils
import get from 'lodash.get';
import omit from 'lodash.omit';
import capitalize from 'lodash.capitalize';
import { getInitials, toArray, toSafeArray } from '~/utils';
import scrollToElement from '~/utils/scrollToElement';

// constants
import { PATHS, API_ENDPOINTS } from '~/constants';

// types
import type { AppTableHeadProps } from '~/components/redesign/AppTable';

interface TestimonialCard {
  avatar_url: string;
  location: string;
  name: string;
  position: string;
  quote: string;
  social_links: Array<{
    type: 'linkedin';
    url: string;
  }>;
}

const { t } = useI18n();
const router = useRouter();
const GqlInstance = useGql();
const isMounted = useMounted();
const { searchParams } = useRequestURL();
const { width: screenWidth } = useWindowSize();
const localePathWithSlash = useLocalePathWithSlash();
const {
  public: { api_v2: baseURL },
} = useRuntimeConfig();

const DESKTOP_WIDTH = 1280;
const JOB_POSTS_PER_PAGE = 10;

const SWIPER_CONFIG: Partial<InstanceType<typeof Swiper>> = {
  slidesPerView: 'auto',
  breakpoints: {
    1280: {
      spaceBetween: 32,
    },
    768: {
      spaceBetween: 24,
    },
    0: {
      spaceBetween: 8,
    },
  },
};

const WHY_OBJECT_FIRST_CARDS = [
  {
    title: t('careers.why-object-first.ul.li-1.strong'),
    icon: 'team',
    description: t('careers.why-object-first.ul.li-1.p'),
  },
  {
    title: t('careers.why-object-first.ul.li-2.strong'),
    icon: 'brain-ai',
    description: t('careers.why-object-first.ul.li-2.p'),
  },
  {
    title: t('careers.why-object-first.ul.li-3.strong'),
    icon: 'handshake',
    description: t('careers.why-object-first.ul.li-3.p'),
  },
  {
    title: t('careers.why-object-first.ul.li-4.strong'),
    icon: 'brain-support',
    description: t('careers.why-object-first.ul.li-4.p'),
  },
];

const GALLERY_TOP_SLIDER_CARDS = [
  {
    src: '/redesign/images/photos/1723496725460.webp',
    alt: t('careers.gallery-top.img-1.alt'),
  },
  {
    src: '/redesign/images/photos/1723496713759.webp',
    alt: t('careers.gallery-top.img-2.alt'),
  },
  {
    src: '/redesign/images/photos/1723496384759.webp',
    alt: t('careers.gallery-top.img-3.alt'),
  },
  {
    src: '/redesign/images/photos/1723496564816.webp',
    alt: t('careers.gallery-top.img-4.alt'),
  },
];

const GALLERY_BOTTOM_SLIDER_CARDS = [
  {
    src: '/redesign/images/photos/unnamed_3_.webp',
    alt: t('careers.gallery-bottom.img-1.alt'),
  },
  {
    src: '/redesign/images/photos/img_3695.webp',
    alt: t('careers.gallery-bottom.img-2.alt'),
  },
  {
    src: '/redesign/images/photos/img_5835_2.webp',
    alt: t('careers.gallery-bottom.img-3.alt'),
  },
  {
    src: '/redesign/images/photos/image_15.webp',
    alt: t('careers.gallery-bottom.img-4.alt'),
  },
];

const VALUES_SLIDER_CARDS = [
  {
    title: t('careers.values.card-1.title'),
    icon: 'reliability-colorfull',
    description: t('careers.values.card-1.description'),
  },
  {
    title: t('careers.values.card-2.title'),
    icon: 'bulb-gear-colorfull',
    description: t('careers.values.card-2.description'),
  },
  {
    title: t('careers.values.card-3.title'),
    icon: 'heart-colorfull',
    description: t('careers.values.card-3.description'),
  },
  {
    title: t('careers.values.card-4.title'),
    icon: 'like-colorfull',
    description: t('careers.values.card-4.description'),
  },
  {
    title: t('careers.values.card-5.title'),
    icon: 'chart-colorfull',
    description: t('careers.values.card-5.description'),
  },
];

const sliderOffset = computed(() => {
  const width = screenWidth.value;

  if (width > DESKTOP_WIDTH) {
    return (width - 1216) / 2;
  } else if (width > 767) {
    return 32;
  } else {
    return 16;
  }
});

const TABLE_HEAD: AppTableHeadProps = [
  {
    key: 'title',
    label: t('Role'),
    width: '22%',
  },
  {
    key: 'team',
    label: t('Team'),
    width: '21.75%',
  },
  {
    key: 'location',
    label: t('Location'),
    width: '22%',
  },
  {
    key: 'type',
    label: t('Type'),
    width: '22%',
  },
  {
    key: 'icon',
    width: '1.5rem',
  },
];

const TEAMS: Record<string, string> = {
  RND: 'R&D',
  GTM: 'GTM',
  MARKETING: 'Marketing',
  SUPPORT: 'Support',
  WEBDEV: 'WebDev',
  SALES: 'Sales',
  IT: 'IT',
  OPERATIONS: 'Operations',
  FINANCE: 'Finance',
  QA: 'QA',
};

const TYPES = ['REMOTE', 'IN_PERSON', 'HYBRID'];

const toSafeRequestVariables = (value: Record<string, string[]>) =>
  Object.entries(value).reduce((acc, [key, value]) => {
    if (value.length) {
      acc[key] = value;
    }

    return acc;
  }, {});

const queryParams = reactive({
  page: Number(searchParams.get('page')) || 1,
  teams: toSafeArray(searchParams.get('teams')),
  types: toSafeArray(searchParams.get('types')),
  locations: toSafeArray(searchParams.get('locations')),
});

const requestVariables = computed(() => ({
  page: queryParams.page,
  ...toSafeRequestVariables(queryParams),
}));

const { data: jobPostsRequestData } = useAsyncData(
  'job-posts',
  () => GqlInstance('JobPostsTable', requestVariables.value),
  {
    watch: [() => requestVariables.value],
  },
);

const { data: locations } = useFetch(API_ENDPOINTS.JOB_POSTS_LOCATIONS, {
  baseURL,
  default: () => [],
});

const jobPosts = computed(() => {
  return get(jobPostsRequestData.value, 'job_posts.data', []);
});
const meta = computed(() => {
  return get(jobPostsRequestData.value, 'job_posts.paginatorInfo', {});
});

const transformTeam = (value: string[]) =>
  toArray(value)
    .map(item => TEAMS[item])
    .join(', ');

const transformLocation = (value: string[]) => value.join(', ');

const transformType = (value: string | string[]) =>
  toArray(value)
    .map(item => item.split('_').map(capitalize).join(' '))
    .join(', ');

// reset page if any was filter changed
watch(Object.values(omit(toRefs(queryParams), 'page')), () => {
  queryParams.page = 1;
});

const openRolesRef = useTemplateRef('openRolesRef');

// update query params if filters were updated
watch(queryParams, async query => {
  router.push({ query });

  await nextTick();

  if (openRolesRef.value) {
    scrollToElement(openRolesRef.value, { block: 'start' });
  }
});

const { data: teamTestimonialsData } = useAsyncGql({
  operation: 'TeamTestimonialCards',
  options: {
    transform: data => data.testimonial_cards.data as TestimonialCard[],
  },
});

const getLinkedInUrl = (socialLinks: TestimonialCard['social_links']) => {
  const linkedInLink = socialLinks?.find(link => link.type === 'linkedin');
  return linkedInLink?.url || '';
};

useApiSeo('careers');
</script>

<template>
  <div data-page-name="careers">
    <section class="our-story">
      <div class="wrapper">
        <h1>{{ $t('careers.our-story.h1') }}</h1>

        <div class="left">
          <h2>{{ $t('careers.our-story.left.h2') }}</h2>
          <p>{{ $t('careers.our-story.left.p') }}</p>

          <Buttons.Link to="#open-roles">
            {{ $t('careers.our-story.left.link') }}
            <SvgIcon name="arrow-forward" />
          </Buttons.Link>
        </div>

        <div class="right">
          <NuxtImg
            :alt="$t('careers.our-story.right.image.alt')"
            preload
            src="/redesign/images/photos/image_18.webp"
            width="592"
            height="291"
            sizes="xs:328px md:704px xl:592px"
            densities="x1 x2"
            format="webp"
          />
        </div>
      </div>
    </section>

    <section class="why-object-first">
      <div class="wrapper">
        <h2>{{ $t('careers.why-object-first.h2') }}</h2>

        <strong class="subtitle">
          {{ $t('careers.why-object-first.strong') }}
        </strong>

        <ul class="cards">
          <li
            v-for="{ title, icon, description } of WHY_OBJECT_FIRST_CARDS"
            :key="title"
            class="card"
          >
            <strong>{{ title }}</strong>
            <SvgIcon :name="icon" />
            <p>{{ description }}</p>
          </li>
        </ul>
      </div>
    </section>

    <div
      :class="['gallery', { 'swiper-no-swiping': screenWidth > DESKTOP_WIDTH }]"
    >
      <Swiper
        v-bind="SWIPER_CONFIG"
        :slides-offset-after="sliderOffset"
        :slides-offset-before="sliderOffset"
      >
        <SwiperSlide
          v-for="{ src, alt } of GALLERY_TOP_SLIDER_CARDS"
          :key="src"
        >
          <NuxtImg
            :src
            :alt
            width="280"
            height="187"
            densities="1x 2x"
            loading="lazy"
            format="webp"
          />
        </SwiperSlide>
      </Swiper>
    </div>

    <section class="together-we-thrive">
      <div class="wrapper">
        <div class="left">
          <h2>{{ $t('careers.together-we-thrive.left.h2') }}</h2>
        </div>

        <div class="right">
          <Buttons.Regular
            :aria-label="$t('Awards.prev-btn.title')"
            fill="outline"
            class="gallery-top-prev-btn"
          >
            <SvgIcon name="arrow-backward" fill="inherit" />
          </Buttons.Regular>

          <Buttons.Regular
            :aria-label="$t('Awards.next-btn.title')"
            fill="outline"
            class="gallery-top-next-btn"
          >
            <SvgIcon name="arrow-forward" fill="inherit" />
          </Buttons.Regular>
        </div>
      </div>

      <Swiper
        v-if="teamTestimonialsData?.length > 0"
        v-bind="SWIPER_CONFIG"
        :modules="[Navigation]"
        :navigation="{
          prevEl: '.gallery-top-prev-btn',
          nextEl: '.gallery-top-next-btn',
        }"
        :slides-offset-after="sliderOffset"
        :slides-offset-before="sliderOffset"
      >
        <SwiperSlide
          v-for="{
            avatar_url,
            location,
            name,
            position,
            quote,
            social_links,
          } of teamTestimonialsData"
          :key="name"
        >
          <blockquote>
            <div class="top">
              <div class="left">
                <SvgIcon name="quote" />
              </div>

              <div class="right">
                <p>{{ quote }}</p>
              </div>
            </div>

            <div class="bottom">
              <div class="left">
                <AppAvatar
                  :src="avatar_url"
                  :alt="`${name} photo`"
                  :initials="getInitials(name)"
                  size="3rem"
                />

                <div class="info">
                  <strong class="name">{{ name }}</strong>
                  <span class="position">{{ position }}</span>

                  <span class="location">
                    <SvgIcon name="location" />

                    {{ location }}
                  </span>
                </div>
              </div>

              <div class="right">
                <NuxtLink
                  v-if="getLinkedInUrl(social_links)"
                  :to="getLinkedInUrl(social_links)"
                  class="linkedIn"
                  target="_blank"
                >
                  <SvgIcon name="linked-in" />
                </NuxtLink>
              </div>
            </div>
          </blockquote>
        </SwiperSlide>
      </Swiper>
    </section>

    <section class="values">
      <div class="wrapper">
        <h2>{{ $t('careers.values.h2') }}</h2>

        <strong>{{ $t('careers.values.strong') }}</strong>

        <ul class="cards">
          <Cards.Values
            v-for="(VALUE, key) of VALUES_SLIDER_CARDS"
            v-bind="VALUE"
            :key
          />
        </ul>
      </div>
    </section>

    <section ref="openRolesRef" id="open-roles">
      <div class="wrapper">
        <h2>{{ $t('careers.open-roles.h2') }}</h2>

        <ul class="mobile-filters">
          <li key="team">
            <MultiselectDropdown
              v-model="queryParams.teams"
              :options="Object.keys(TEAMS)"
              :label="$t('Team')"
              size="small"
              fill="outline"
            >
              <template #value="{ value }">
                {{ transformTeam(value) }}
              </template>

              <template #item="{ value }">
                {{ transformTeam(value) }}
              </template>
            </MultiselectDropdown>
          </li>

          <li key="location">
            <MultiselectDropdown
              v-model="queryParams.locations"
              :options="locations"
              :label="$t('Location')"
              size="small"
              fill="outline"
            />
          </li>

          <li key="type">
            <MultiselectDropdown
              v-model="queryParams.types"
              :options="TYPES"
              :label="$t('Type')"
              size="small"
              fill="outline"
            >
              <template #value="{ value }">
                {{ transformType(value) }}
              </template>

              <template #item="{ value }">
                {{ transformType(value) }}
              </template>
            </MultiselectDropdown>
          </li>
        </ul>

        <AppTable.Head :head="TABLE_HEAD">
          <template #team="{ value: label }">
            <MultiselectDropdown
              v-model="queryParams.teams"
              :options="Object.keys(TEAMS)"
              :label
              size="small"
              fill="outline"
            >
              <template #value="{ value }">
                {{ transformTeam(value) }}
              </template>

              <template #item="{ value }">
                {{ transformTeam(value) }}
              </template>
            </MultiselectDropdown>
          </template>

          <template #location="{ value: label }">
            <MultiselectDropdown
              v-model="queryParams.locations"
              :options="locations"
              :label
              size="small"
              fill="outline"
            />
          </template>

          <template #type="{ value: label }">
            <MultiselectDropdown
              v-model="queryParams.types"
              :options="TYPES"
              :label
              size="small"
              fill="outline"
            >
              <template #value="{ value }">
                {{ transformType(value) }}
              </template>

              <template #item="{ value }">
                {{ transformType(value) }}
              </template>
            </MultiselectDropdown>
          </template>
        </AppTable.Head>

        <AppTable.Table
          v-if="jobPosts.length"
          :head="TABLE_HEAD"
          :content="jobPosts"
        >
          <template #row="rowData">
            <Buttons.Link
              :to="localePathWithSlash(PATHS.CAREERS.path + rowData.slug)"
              target="_blank"
              fill="none"
              accent="black"
              class="row"
            >
              <AppTable.Row :head="TABLE_HEAD" :row-data>
                <template #title-value="{ value }">
                  <strong class="role">
                    {{ value }}
                  </strong>
                </template>

                <template #team-value="{ value }">
                  {{ transformTeam(value) }}
                </template>

                <template #location-value="{ value }">
                  {{ transformLocation(value) }}
                </template>

                <template #type-value="{ value }">
                  {{ transformType(value) }}
                </template>

                <template #icon-value>
                  <SvgIcon name="arrow-forward" fill="inherit" />
                </template>
              </AppTable.Row>
            </Buttons.Link>
          </template>
        </AppTable.Table>

        <div v-else-if="isMounted" class="not-found">
          <strong>{{ $t('careers.open-roles.not-found.strong') }}</strong>
          <p>{{ $t('careers.open-roles.not-found.p') }}</p>
        </div>

        <AppPagination
          v-if="Number(meta.lastPage) > 1"
          v-model="queryParams.page"
          :total-items="meta.total"
          :items-per-page="JOB_POSTS_PER_PAGE"
          :max-pages-shown="4"
        />
      </div>
    </section>

    <div
      :class="['gallery', { 'swiper-no-swiping': screenWidth > DESKTOP_WIDTH }]"
    >
      <Swiper
        v-bind="SWIPER_CONFIG"
        :slides-offset-after="sliderOffset"
        :slides-offset-before="sliderOffset"
      >
        <SwiperSlide
          v-for="{ src, alt } of GALLERY_BOTTOM_SLIDER_CARDS"
          :key="src"
        >
          <NuxtImg
            :src
            :alt
            width="280"
            height="187"
            densities="1x 2x"
            loading="lazy"
            format="webp"
          />
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/mixins/text-transform';
@use '_/variables/shadow';
@use '_/fn';

.wrapper {
  padding: 0 2rem;
  @include size.wrapper(80rem);

  @include media.mobile {
    padding: 0 1rem;
  }
}

[data-page-name='careers'] {
  padding: 4.56rem 0 10.27rem 0;

  @include media.tablet {
    padding: 4rem 0 11.75rem 0;
  }

  @include media.mobile {
    padding: 1.5rem 0 5rem 0;
  }

  section.our-story {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 5rem;
    }

    .wrapper {
      @include flex.start-sb;
      flex-wrap: wrap;
      gap: 5rem 0;

      @include media.tablet {
        flex-direction: column;
        gap: 1.5rem 0;
      }

      h1 {
        width: 100%;
        @include typo.h2;
      }

      .left,
      .right {
        width: calc(50% - 1rem);

        @include media.tablet {
          width: 100%;
        }
      }

      .left {
        @include flex.start-start;
        flex-direction: column;
        gap: 1.5rem 0;

        h2 {
          @include typo.h3;
        }

        p {
          @include typo.body-2;
        }

        [data-component-name='Buttons.Link'] {
          @include media.mobile {
            width: 100%;
          }
        }
      }

      .right {
        img {
          @include media.tablet {
            width: 100%;
            height: auto;
          }
        }
      }
    }
  }

  section.why-object-first {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 5rem;
    }

    .wrapper {
      @include flex.start-sb;
      flex-direction: column;
      gap: 2rem 0;

      @include media.tablet {
        gap: 1.5rem 0;
      }

      h2 {
        @include typo.h3;
      }

      strong {
        @include typo.subtitle-1;
      }

      ul.cards {
        @include flex.stretch-sb;
        flex-wrap: wrap;
        gap: 2rem;

        @include media.tablet {
          gap: 1.5rem;
        }

        li.card {
          @include flex.start-start;
          flex-direction: column;
          gap: 1.5rem 0;

          box-shadow: shadow.$elevation-1;
          width: calc(50% - 1rem);
          padding: 2rem;

          @include media.tablet {
            padding: 1.5rem;
            width: calc(50% - 0.75rem);
          }

          @include media.mobile {
            width: 100%;
            padding: 1.5rem 1rem;
          }

          strong {
            @include typo.subtitle-1;
          }

          svg {
            @include size.fixed(3.125rem);
          }

          p {
            @include typo.body-2;
          }
        }
      }
    }
  }

  .gallery {
    &:first-of-type {
      margin-bottom: 10.37rem;

      @include media.tablet {
        margin-bottom: 5rem;
      }
    }

    &:deep(.swiper) {
      .swiper-wrapper {
        .swiper-slide {
          @include size.fixed-width(17.5rem);

          img {
            display: block;
          }
        }
      }
    }
  }

  section.together-we-thrive {
    .wrapper {
      @include flex.center-sb;

      @include media.mobile {
        @include flex.stretch-start;
        flex-direction: column;
        gap: 0.5rem 0;
      }

      .left {
        h2 {
          @include typo.h3;
        }
      }

      .right {
        @include flex.stretch-sb;
        gap: 0 1rem;

        @include media.mobile {
          gap: 0 0.5rem;
        }

        [data-component-name='Buttons.Regular'] {
          @include media.mobile {
            width: 100%;
          }

          svg {
            @include size.fixed-width(1.5rem);
          }
        }
      }
    }

    &:deep(.swiper) {
      .swiper-wrapper {
        padding: 2rem 0 10.25rem 0;

        @include media.tablet {
          padding: 1.5rem 0 5rem 0;
        }

        @include media.mobile {
          padding: 1.5rem 0 3.5rem 0;
        }

        .swiper-slide {
          display: flex;
          height: auto;

          @include size.fixed-width(56.5rem);

          @include media.tablet {
            @include size.fixed-width(44rem);
          }

          @include media.mobile {
            @include size.fixed-width(20.5rem);
          }

          blockquote {
            width: 100%;
            min-height: 18.18rem;
            padding: 2rem;
            box-shadow: shadow.$elevation-1;

            @include flex.stretch-sb;
            flex-direction: column;

            @include media.tablet {
              min-height: 18.8rem;
              padding: 1.5rem;
            }

            @include media.mobile {
              min-height: 27rem;
              padding: 1.5rem 1rem;
            }

            .top {
              @include flex.start-sb;
              gap: 0 1rem;
              margin-bottom: 2rem;

              @include media.tablet {
                margin-bottom: 1.5rem;
              }

              .left {
                svg {
                  display: block;
                  @include size.fixed(1.5rem);
                }
              }

              .right {
                width: 100%;

                p {
                  @include typo.body-1;
                }
              }
            }

            .bottom {
              margin-left: 2.5rem;
              @include flex.center-sb;

              @include media.mobile {
                flex-direction: column;
                margin-left: 0;
                gap: 0.5rem 0;
              }

              .left {
                @include flex.center-start;
                gap: 0 0.5rem;

                @include media.mobile {
                  align-self: flex-start;
                }

                .info {
                  @include flex.start-start;
                  flex-direction: column;

                  strong.name {
                    @include typo.subtitle-2;
                  }

                  span.position,
                  span.location {
                    @include typo.body-4;
                    color: fn.token('text-secondary');
                  }

                  span.location {
                    @include flex.center-start;

                    svg {
                      margin-right: 0.25rem;
                      @include size.fixed(1rem);
                    }
                  }
                }
              }

              .right {
                @include media.mobile {
                  align-self: flex-end;
                }

                a.linkedIn {
                  svg {
                    display: block;
                    @include size.fixed(2rem);
                    fill: fn.token('surf-cont-secon-action');
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  section.values {
    .wrapper {
      @include flex.start-start;
      flex-direction: column;
      gap: 2rem 0;

      margin-bottom: 5rem;

      @include media.tablet {
        gap: 1.5rem 0;
        margin-bottom: 0rem;
      }

      @include media.mobile {
        gap: 0.5rem 0;
        gap: 1.5rem 0;
        flex-direction: column;
      }

      h2 {
        @include typo.h3;
      }

      strong {
        @include typo.body-2;
      }

      ul.cards {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 2rem;

        @include media.tablet {
          grid-template-columns: repeat(3, 1fr);
        }

        @include media.mobile {
          grid-template-columns: 1fr;
        }
      }
    }
  }

  section#open-roles {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 5rem;
    }

    .wrapper {
      h2 {
        @include typo.h3;
        padding-top: 5rem;
        margin-bottom: 2rem;
      }

      ul.mobile-filters {
        display: none;

        @include media.mobile {
          @include flex.stretch-start;
          flex-direction: column;
          gap: 1rem 0;
          margin-bottom: 1.5rem;
        }
      }

      [data-component-name='AppTable.Head'] {
        @include media.mobile {
          display: none;
        }
      }

      [data-component-name='AppTable.Table'] {
        margin-bottom: 5rem;

        @include media.tablet {
          margin-bottom: 4rem;
        }

        @include media.mobile {
          margin-bottom: 1.5rem;
        }

        [data-component-name='Buttons.Link'] {
          display: block;

          &:deep([data-component-name='Buttons.Base']) {
            display: block;
            padding: 0;

            strong.role {
              word-break: break-word;
              @include typo.subtitle-3;
            }

            svg {
              display: block;
              fill: fn.token('link');

              @include media.mobile {
                margin-left: auto;
              }
            }
          }
        }
      }

      .not-found {
        padding-top: 2.5rem;

        @include flex.center-end;
        flex-direction: column;

        strong {
          @include typo.subtitle-1;
        }

        p {
          @include typo.body-2;
        }
      }
    }
  }
}
</style>
